/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { FC, useMemo, useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import usePortal from "react-useportal";

// antd:
// import { CloseOutlined } from "@ant-design/icons";
import { Modal } from "antd";

// eslint-disable-next-line node/no-missing-import
import type * as CSS from "csstype";

// Store
import store from "../../libs/store.ts";

// My Components:
import ModalWindow from "../react/ModalWindow.tsx";
import AppIcons from "../react/AppIcons.tsx";

import ModalFullscreen from "../react/ModalFullscreen.tsx";
// Redux-hooks:
import { useAppSelector, useAppDispatch } from "../../libs/redux-hooks.ts";
// Reducers:
import { getLng, getTheme, getDict, getDesktopType, getAppState, changeAppState, getUser, setGlobalMask, getSafePtr } from "../../libs/reducer.ts";

// My functions
import fnc from "../../libs/fnc.ts";
// Types:
import type {TuniversalElementProps} from "../../types/Iuigen.d.ts";
import type {Tpermissions} from "../../types/easyTypes.ts";

// const { Option } = Select;
const { rz, $ } = window;

type Tmodal = {
  closeOnOutsideClick:boolean,
  closeOnEsc:boolean,
  width?:number,
  height?:number,
  isFitContentHeight?:boolean,
  minWidth:number,
  minHeight:number,
  antdModal?:boolean,
  windowModal?:boolean,
  fullScreen?:boolean,
  isDisableCloseButton?:boolean,
  isHiddenCloseButton?:boolean,
  mask?:boolean,
  autoFocusButton:null | "ok" | "cancel",
  resizeable?:boolean,
  moveable?:boolean,
  keysMovable?:boolean,
  maximize?:boolean,
  zIndex?:number
};

const RZmodal:FC<TuniversalElementProps> = ({attributes, children}) => {
  RZmodal.displayName = "Modal";
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const user = useAppSelector(getUser);
  const lng = useAppSelector(getLng);
  const theme = useAppSelector(getTheme);
  const dct = useAppSelector(getDict);
  const isMobile = useAppSelector(getDesktopType);
  const safe = useAppSelector(getSafePtr);
  const rlng = (lng === "en" ? "ru" : "en");

  const {rootAttributes, key, fullPath, type} = attributes; // onClick, onChange, onRightClick, onHover, onLoad
  const appState = useAppSelector(getAppState(rootAttributes.key));
  const elState = appState[fullPath];
  const {icon, iconClass, iconColor, color, backgroundColor, maximize} = elState.attributes;
  const zIndexState = elState.specialParams && elState.specialParams.zIndex;
  const rootNamespace = useMemo(() => (window[rootAttributes.key || rz.strMakeID(24)]), [rootAttributes.key]);

  const permissions = useMemo<Tpermissions>(() => (fnc.checkPermissions(user, attributes)), [user, attributes]);

  const docSize = rz.documentSize(document);

  const modalType = useMemo<Tmodal>(() => {
    const typ:Tmodal = {closeOnOutsideClick: false, closeOnEsc: false, autoFocusButton: "ok", moveable: true, minWidth: 624, minHeight: 325, maximize: true, resizeable: true, keysMovable: false};
    (type || "").replace((/(\s+|_|\|)/igm), " ").split(" ").forEach((tt) => {
      if (tt === "small") { typ.minWidth = 416 }
      else if (tt === "medium") { typ.minWidth = 624 }
      else if (tt === "large") { typ.minWidth = 846 }
      else if (tt === "mask") { typ.mask = true }
      else if (~["cancelFocus", "cancelfocus", "autoFocusCancel"].indexOf(tt)) { typ.autoFocusButton = "cancel" }
      else if (~["okFocus", "okfocus", "autoFocusOk"].indexOf(tt)) { typ.autoFocusButton = "ok" }
      else if (~["closeOnOutsideClick", "outsideClick", "outsideclick"].indexOf(tt)) { typ.closeOnOutsideClick = true }
      else if (~["closeOnEsc", "closeonesc"].indexOf(tt)) { typ.closeOnEsc = true }
      else if (~["fullscreen", "fullScreen", "mobile"].indexOf(tt)) { typ.fullScreen = true }
      else if (~["window", "windowModal"].indexOf(tt)) { typ.windowModal = true }
      else if (~["resizeable", "resizeablewindow", "resizeableWindow"].indexOf(tt)) { typ.resizeable = true; typ.windowModal = true }
      else if (~["moveable"].indexOf(tt)) { typ.moveable = true; typ.windowModal = true }
      else if (~["keysmoveable", "keysMovable"].indexOf(tt)) { typ.keysMovable = true; typ.moveable = true; typ.windowModal = true }
      else if (~["maximize"].indexOf(tt)) { typ.maximize = true; typ.windowModal = true }
      else if (~["disableCloseButton", "disableclosebutton"].indexOf(tt)) { typ.isDisableCloseButton = true }
      else if (~["notClosable", "hideclosebutton", "hideCloseButton"].indexOf(tt)) { typ.isHiddenCloseButton = true }
      else if (~["antd-modal", "antdmodal", "antdModal"].indexOf(tt)) { typ.antdModal = true }
      // else if (~["antd-info", "antdinfo", "antdInfo"].indexOf(tt)) { typ.antd = "info" }
    });

    if (attributes?.minwidth || attributes?.minWidth) { typ.minWidth = (rz.isNumeric(attributes.minwidth || attributes.minWidth) ? Number(attributes.minwidth || attributes.minWidth) : attributes.minwidth || attributes.minWidth) }
    if (attributes?.minheight || attributes?.minHeight) { typ.minHeight = (rz.isNumeric(attributes.minheight || attributes.minHeight) ? Number(attributes.minheight || attributes.minHeight) : attributes.minheight || attributes.minHeight) }

    if (attributes?.width) {
      if (rz.isNumeric(attributes.width)) { typ.width = Number(attributes.width) }
      else if (attributes.width === "auto") {
        // eslint-disable-next-line prefer-destructuring
        let width = docSize.width; if (width) width -= 100;
        // const aw = $("#virtual-desktop"); if (aw[0]) width -= (parseInt($(aw[0]).css("padding-left"), 10) + parseInt($(aw[0]).css("padding-right"), 10));
        if (!width || width < typ.minWidth) width = typ.minWidth; // Minimal height
        typ.width = width;
      }
    }
    if (attributes?.height) {
      if (rz.isNumeric(attributes.height)) { typ.height = Number(attributes.height) }
      else if (attributes.height === "auto") {
        // eslint-disable-next-line prefer-destructuring
        let height = docSize.height; if (height) height -= 100;
        const ah = $(".rz-layout-header"); if (ah[0]) height -= ($(ah[0]).height() + parseInt($(ah[0]).css("padding-top"), 10) + parseInt($(ah[0]).css("padding-bottom"), 10));
        if (!height || height < typ.minHeight) height = typ.minHeight; // Minimal height
        typ.height = height;
      } else if (attributes.height === "fit-content") {
        typ.isFitContentHeight = true;
      }
    }

    if (attributes?.size) { typ.width = (attributes.size === "large" ? 846 : (attributes.size === "medium" ? 624 : 416)) }
    if (attributes?.closeOnOutsideClick) { typ.closeOnOutsideClick = (attributes.closeOnOutsideClick === "true") }
    if (attributes?.closeOnEsc) { typ.closeOnEsc = (attributes.closeOnEsc === "true") }
    if (attributes?.mask) { typ.mask = (attributes.mask === "true") }
    if (attributes?.resizeable) { typ.resizeable = (attributes.resizeable === "true") }
    if (attributes?.moveable) { typ.moveable = (attributes.moveable === "true") }
    if (attributes?.maximize) { typ.maximize = (attributes.maximize === "true") }
    if (attributes?.disableCloseButton || attributes?.disableclosebutton) { typ.isDisableCloseButton = (attributes.disableCloseButton === "true" || attributes.disableclosebutton === "true") }
    if (attributes?.hideCloseButton || attributes?.hideclosebutton) { typ.isHiddenCloseButton = (attributes.hideCloseButton === "true" || attributes.hideclosebutton === "true") }
    if (attributes?.fullScreen || attributes?.fullscreen) { typ.fullScreen = (attributes.fullScreen === "true" || attributes?.fullscreen === "true") }
    if (attributes?.keysmovable || attributes?.keysMovable) { typ.keysMovable = (attributes.keysmovable === "true" || attributes?.keysMovable === "true") }
    if (attributes?.window || attributes?.windowModal) { typ.windowModal = (attributes.window === "true" || attributes?.windowModal === "true") }
    if (attributes?.zindex || attributes?.zIndex) { typ.zIndex = Number(attributes.zindex || attributes.zIndex) }
    return typ;
  }, [type, attributes, docSize.height, docSize.width]);

  const { ref, openPortal, closePortal, isOpen, Portal } = usePortal({closeOnOutsideClick: modalType.closeOnOutsideClick, closeOnEsc: modalType.closeOnEsc});
  const portalRef = useRef(null);

  useEffect(() => {
    if (modalType.fullScreen || modalType.windowModal) {
      if (elState.visible && !isOpen && (permissions.visible !== false)) { dispatch(setGlobalMask(true)); openPortal() }
      else if (!elState.visible && isOpen) { dispatch(setGlobalMask(false)); closePortal() }
    }
  }, [elState.visible, permissions.visible, closePortal, isOpen, openPortal, modalType.fullScreen, modalType.windowModal, dispatch]);

  const clsu = useMemo<Array<string>>(() => {
    const userClss = rz.objClone(elState?.userClass || []);
    if (!rz.arrContains(userClss, "-overflow-")) { userClss.push("uk-overflow-hidden") }
    if (modalType.windowModal && !rz.arrContains(userClss, (/^rz-theme-/igm))) { userClss.push("rz-theme-d5") }
    return userClss;
  }, [elState?.userClass, modalType.windowModal]);

  const styl = useMemo<CSS.Properties>(() => {
    const est = {...elState.userStyle};
    if ((elState?.visible === false || permissions.visible === false) && est.display !== "none") { est.display = "none" }
    else if ((elState?.visible === true && permissions.visible !== false) && est.display === "none") { est.display = undefined }
    if (!est.position) { est.position = "static" }
    if (!est.height && modalType.fullScreen) { est.height = "100%" }
    let clr:string; const allc = (color || "").split("|"); if (allc.length > 1) { clr = (theme === "dark" ? allc[1] : allc[0]) } else { clr = allc[0] }
    if (!est?.color && clr) { est.color = clr }
    let bclr:string; const allb = (backgroundColor || "").split("|"); if (allb.length > 1) { bclr = (theme === "dark" ? allb[1] : allb[0]) } else { bclr = allb[0] }
    if (!est?.backgroundColor && bclr) { est.backgroundColor = bclr }
    return est;
  }, [elState.userStyle, elState?.visible, permissions.visible, modalType.fullScreen, color, backgroundColor, theme]);

  // eslint-disable-next-line react-hooks/exhaustive-deps, max-len
  const title = useMemo<string | undefined>(() => (elState.attributes["title_" + lng] || elState.attributes.title || elState.attributes["title_" + rlng] || attributes["title_" + lng] || attributes.title || attributes["title_" + rlng]), [elState.attributes, attributes, lng]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const tooltip = useMemo<string | undefined>(() => (elState.attributes["tooltip_" + lng] || elState.attributes.tooltip || elState.attributes["tooltip_" + rlng]), [elState.attributes, lng]);

  const rico = useMemo(() => {
    let ico:React.ReactElement | undefined;
    if (icon) { ico = (<AppIcons key={key + "-icon"} icopath={icon || ""} attrs={{className: iconClass, color: iconColor}} uk-tooltip={tooltip} />) }
    return ico;
  }, [icon, key, iconClass, iconColor, tooltip]);

  const footer = useMemo(() => {
    let ftr:Array<React.ReactElement> | undefined;
    if (attributes?.footer) { ftr = attributes?.footer }
    return ftr;
  }, [attributes?.footer]);

  const onClosePortal = useCallback(() => {
    if (elState.visible) {
      if (fnc.config?.isDev) console.log({action: "<Modal key='" + key + "'> -> onClosePortal()"});
      if (rootNamespace && (attributes.onClose || attributes.onclose) && rootNamespace[attributes.onClose || attributes.onclose]) {
        const clientFNC = rootNamespace[attributes.onClose || attributes.onclose];
        if (rz.isPromise(clientFNC)) {
          clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, isOpen, safe}).then((ret) => {
            if (rz.isUndefinedOrNull(ret)) { return }
            if (ret && ret.err) { console.log(ret.err) }
            if (ret?.state?.fullPath && ret?.state?.newState) {
              dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
            }
            if (ret && ret.navigate) { navigate(ret.navigate.to, {replace: Boolean(ret.navigate.replace)}); console.log("navigate(" + ret.navigate.to + ")") }
          });
        }
        else if (rz.isFunction(clientFNC)) {
          const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, isOpen, safe}); if (rz.isUndefinedOrNull(ret)) { return }
          if (ret && ret.err) { console.log(ret.err) }
          if (ret?.state?.fullPath && ret?.state?.newState) {
            dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
          }
          if (ret && ret.navigate) { navigate(ret.navigate.to, {replace: Boolean(ret.navigate.replace)}); console.log("navigate(" + ret.navigate.to + ")") }
        }
      }
      else {
        dispatch(changeAppState({appkey: rootAttributes.key, fullPath, newState: {...elState, visible: false}}));
      }
    }
  }, [elState, key, rootNamespace, attributes, rootAttributes.key, user, lng, dct, isOpen, dispatch, navigate, fullPath, safe]);

  const onOkClick = useCallback(() => {
    if (isOpen && elState.visible) {
      if (fnc.config?.isDev) console.log({action: "<Modal key='" + key + "'> -> onOkClick()"});
      if (rootNamespace && (attributes.onOk || attributes.onok) && rootNamespace[attributes.onOk || attributes.onok]) {
        const clientFNC = rootNamespace[attributes.onOk || attributes.onok];
        if (rz.isPromise(clientFNC)) {
          clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, isOpen, safe}).then((ret) => {
            if (rz.isUndefinedOrNull(ret)) { return }
            if (ret && ret.err) { console.log(ret.err) }
            if (ret?.state?.fullPath && ret?.state?.newState) {
              dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
            }
            if (ret && ret.navigate) { navigate(ret.navigate.to, {replace: Boolean(ret.navigate.replace)}); console.log("navigate(" + ret.navigate.to + ")") }
          });
        }
        else if (rz.isFunction(clientFNC)) {
          const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, isOpen, safe}); if (rz.isUndefinedOrNull(ret)) { return }
          if (ret && ret.err) { console.log(ret.err) }
          if (ret?.state?.fullPath && ret?.state?.newState) {
            dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
          }
          if (ret && ret.navigate) { navigate(ret.navigate.to, {replace: Boolean(ret.navigate.replace)}); console.log("navigate(" + ret.navigate.to + ")") }
        }
      }
      else {
        dispatch(changeAppState({appkey: rootAttributes.key, fullPath, newState: {...elState, visible: false}}));
      }
    }
  }, [isOpen, elState, key, rootNamespace, attributes, rootAttributes.key, user, lng, dct, dispatch, navigate, fullPath, safe]);

  // Global Modal API to use it inside the clients-script:
  const modalShow = (params:{appkey:string, modalPath?:string, fullPath?:string}) => {
    if (params && params.appkey) {
      const mPath = params.modalPath || params.fullPath, {ar} = store.getState();
      // @ts-ignore
      if (mPath && ar.uiState && ar.uiState[params.appkey] && ar.uiState[params.appkey][mPath] && ar.uiState[params.appkey][mPath].visible !== true) {
        dispatch(changeAppState({ appkey: params.appkey, fullPath: mPath, newState: {...ar!.uiState[params.appkey][mPath], visible: true} }));
      }
    }
  };
  const modalHide = (params:{appkey:string, modalPath?:string, fullPath?:string}) => {
    if (params && params.appkey) {
      const mPath = params.modalPath || params.fullPath, {ar} = store.getState();
      if (mPath && ar.uiState && ar.uiState[params.appkey] && ar.uiState[params.appkey][mPath] && ar.uiState[params.appkey][mPath].visible !== false) {
        dispatch(changeAppState({ appkey: params.appkey, fullPath: mPath, newState: {...ar.uiState[params.appkey][mPath], visible: false} }));
      }
    }
  };
  const modalIsOpen = (params:{appkey:string, modalPath?:string, fullPath?:string}) => {
    if (params && params.appkey) {
      const mPath = params.modalPath || params.fullPath, {ar} = store.getState();
      if (mPath && ar.uiState && ar.uiState[params.appkey] && ar.uiState[params.appkey][mPath]) {
        return Boolean(ar.uiState[params.appkey][mPath].visible);
      }
    }
    return null;
  };
  // ++++++++++++++++++++++++++++++++
  useEffect(() => {
    // @ts-ignore
    if (!window.modalShow) { window.modalShow = modalShow }
    // @ts-ignore
    if (!window.modalHide) { window.modalHide = modalHide }
    // Aliases
    // @ts-ignore
    if (!window.modalOpen) { window.modalOpen = modalShow }
    // @ts-ignore
    if (!window.modalClose) { window.modalClose = modalHide }
    // @ts-ignore
    if (!window.modalIsOpen) { window.modalIsOpen = modalIsOpen }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // ++++++++++++++++++++++++++++++++

  if (modalType.antdModal) {
    return (
      <Modal key={key}
        open={elState.visible === true}
        title={title}
        mask={modalType.mask}
        maskClosable={modalType.closeOnOutsideClick}
        keyboard={modalType.closeOnEsc}
        closable={modalType.isHiddenCloseButton !== true}
        footer={footer}
        onCancel={onClosePortal}
        onOk={onOkClick}
        destroyOnHidden={true}
        zIndex={zIndexState || modalType.zIndex || 900}
        // autoFocusButton={modalType.autoFocusButton}
        // afterClose={onClosePortal}
      >
        {children}
      </Modal>
    );
  }

  return (
    <React.Fragment key={key + "-fragment"} >
      <div key={key + "-dummy-div"} ref={ref} style={{display: "none"}} />
      {isOpen && (
        <Portal key={key + "-portal"} ref={portalRef} style={{height: "100vh", width: "100vw"}} >
          { modalType.fullScreen ? (
            <ModalFullscreen modalKey={key} icon={rico} title={title} closePortal={onClosePortal} className={clsu.join(" ")} style={styl}
              isDisableCloseButton={modalType.isDisableCloseButton}
              isHiddenCloseButton={modalType.isHiddenCloseButton}
              closeOnEsc={modalType.closeOnEsc}
              zIndex={zIndexState || modalType.zIndex || 900}
            >
              {children}
            </ModalFullscreen>
          ) : (
            <ModalWindow modalKey={key}
              isOpen={Boolean(elState.visible)} onRequestClose={onClosePortal}
              isClosableMask={modalType.closeOnOutsideClick}
              initWidth={modalType.width || modalType.minWidth} initHeight={modalType.isFitContentHeight ? "fit-content" : (modalType.height || modalType.minHeight)}
              minWidth={modalType.minWidth} minHeight={modalType.minHeight}
              title={title} icon={rico} dct={dct}
              isDisableCloseButton={modalType.isDisableCloseButton} isHiddenCloseButton={modalType.isHiddenCloseButton} closeOnEsc={modalType.closeOnEsc}
              hasResizeable={!isMobile && modalType.resizeable}
              hasMoveable={!isMobile && modalType.moveable} keysMovable={modalType.moveable && modalType.keysMovable}
              hasMaximize={!isMobile && modalType.maximize} isMaximize={isMobile || (maximize === true || maximize === "true")}
              className={clsu.join(" ")} style={styl}
              zIndex={zIndexState || modalType.zIndex || 900}
            >
              {children}
            </ModalWindow>
          )}
        </Portal>
      )}
    </React.Fragment>
  );
}

export default RZmodal;
/* eslint-enable @typescript-eslint/no-unused-vars */
